import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  IconMicrophone,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlayerStop,
  IconRefresh,
  IconTrash,
  IconMicrophoneOff,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MLoader from "@/components/ui/atoms/MLoader";
import {
  useVoiceNoteRecorder,
  RecorderState,
} from "@/hooks/useVoiceNoteRecorder";
import styles from "./VoiceNoteRecorder.module.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface VoiceNoteRecorderProps {
  /** Blob to hand back to parent when a take is ready (null when discarded) */
  onBlobReady: (blob: Blob | null) => void;
  /** Called on every state transition so parent can gate the Done button */
  onStateChange: (s: RecorderState) => void;
  /** True when form is in edit mode and a saved note already exists */
  hasSavedNote?: boolean;
  /** Duration of existing saved note in seconds (edit mode) */
  savedDuration?: number;
  /** Lazy signed URL fetch for edit-mode mini-player playback */
  onFetchSavedUrl?: () => Promise<string>;
  /** Passed from parent handleSubmit — disables controls while uploading */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const VoiceNoteRecorder: React.FC<VoiceNoteRecorderProps> = ({
  onBlobReady,
  onStateChange,
  hasSavedNote,
  savedDuration,
  onFetchSavedUrl,
  disabled,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    state,
    wavBlob,
    elapsed,
    micError,
    startRecording,
    stopRecording,
    discard,
    reRecord,
  } = useVoiceNoteRecorder();

  // ---------------------------------------------------------------------------
  // Preview audio element (State 3 — new recording)
  // T-56-05: blob URL created on entering preview, revoked on leaving / unmount
  // ---------------------------------------------------------------------------
  const previewAudioRef = useRef<HTMLAudioElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [previewElapsed, setPreviewElapsed] = useState(0);

  // Create / revoke object URL when entering or leaving preview
  useEffect(() => {
    if (state === "preview" && wavBlob) {
      const url = URL.createObjectURL(wavBlob);
      previewUrlRef.current = url;
      if (previewAudioRef.current) {
        previewAudioRef.current.src = url;
        previewAudioRef.current.load();
      }
      return () => {
        URL.revokeObjectURL(url);
        previewUrlRef.current = null;
        setIsPreviewPlaying(false);
        setPreviewElapsed(0);
      };
    }
  }, [state, wavBlob]);

  // Elapsed ticker during preview playback
  useEffect(() => {
    if (!isPreviewPlaying || !previewAudioRef.current) return;
    const interval = setInterval(() => {
      setPreviewElapsed(Math.floor(previewAudioRef.current?.currentTime ?? 0));
    }, 500);
    return () => clearInterval(interval);
  }, [isPreviewPlaying]);

  const handlePreviewPlayPause = useCallback(() => {
    if (!previewAudioRef.current) return;
    if (isPreviewPlaying) {
      previewAudioRef.current.pause();
      setIsPreviewPlaying(false);
    } else {
      previewAudioRef.current.play();
      setIsPreviewPlaying(true);
    }
  }, [isPreviewPlaying]);

  const handleDiscard = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setIsPreviewPlaying(false);
    setPreviewElapsed(0);
    discard();
  }, [discard]);

  // ---------------------------------------------------------------------------
  // Edit-mode mini-player (T-56-06: lazy signed URL, cached in ref)
  // ---------------------------------------------------------------------------
  const editAudioRef = useRef<HTMLAudioElement>(null);
  const editUrlRef = useRef<string | null>(null);
  const [isEditPlaying, setIsEditPlaying] = useState(false);

  const handleEditPlayPause = useCallback(async () => {
    if (!editAudioRef.current) return;
    if (isEditPlaying) {
      editAudioRef.current.pause();
      setIsEditPlaying(false);
      return;
    }
    // Lazy-fetch signed URL on first play
    if (!editUrlRef.current && onFetchSavedUrl) {
      const url = await onFetchSavedUrl();
      editUrlRef.current = url;
      editAudioRef.current.src = url;
      editAudioRef.current.load();
    }
    editAudioRef.current.play();
    setIsEditPlaying(true);
  }, [isEditPlaying, onFetchSavedUrl]);

  // ---------------------------------------------------------------------------
  // Parent callbacks: blob + state
  // ---------------------------------------------------------------------------
  useEffect(() => {
    onBlobReady(wavBlob);
  }, [wavBlob, onBlobReady]);

  // Track elapsed in a ref so the state-transition effect can read it synchronously
  const elapsedRef = useRef(0);
  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  const prevStateRef = useRef<RecorderState>("idle");
  useEffect(() => {
    onStateChange(state);
    // Auto-stop toast (D-56-12): transitioning recording→preview at ≥55s means
    // the recorder hit the time limit, not a manual stop.
    if (
      prevStateRef.current === "recording" &&
      state === "preview" &&
      elapsedRef.current >= 55
    ) {
      showToast({
        message: t("healthMemory.voiceNote.maxLengthToast"),
        variant: "info",
      });
    }
    prevStateRef.current = state;
  }, [state, onStateChange, showToast, t]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={styles.recorderRoot}>
      {/* Inline mic-permission error (VN-05) — NOT a toast; parent form stays usable */}
      {micError && (
        <div className={styles.micErrorRow}>
          <IconMicrophoneOff
            size={18}
            color="var(--colour-text-error, #C33F58)"
          />
          <span className={styles.micErrorText}>{micError}</span>
        </div>
      )}

      {/* State 1: Idle — show "Add voice note" only when no saved note exists */}
      {state === "idle" && !hasSavedNote && (
        <MButton
          variant="outline"
          size="sm"
          onClick={startRecording}
          disabled={disabled}
          leftIcon={<IconMicrophone size={20} />}
        >
          {t("healthMemory.voiceNote.addLabel")}
        </MButton>
      )}

      {/* Edit-mode hybrid: idle state with an existing saved note */}
      {state === "idle" && hasSavedNote && (
        <div className={styles.recordingRow}>
          <MButton
            variant="icon-only-outline"
            size="sm"
            onClick={handleEditPlayPause}
            disabled={disabled}
            aria-label={
              isEditPlaying
                ? t("healthMemory.voiceNote.pauseAriaLabel")
                : t("healthMemory.voiceNote.playAriaLabel")
            }
            leftIcon={
              isEditPlaying ? (
                <IconPlayerPause size={20} />
              ) : (
                <IconPlayerPlay size={20} />
              )
            }
          />
          {savedDuration !== undefined && (
            <span className={styles.timer}>{formatTime(savedDuration)}</span>
          )}
          {/* Hidden audio element for edit-mode playback (T-56-06) */}
          <audio ref={editAudioRef} onEnded={() => setIsEditPlaying(false)} />
          <MButton
            variant="outline"
            size="sm"
            onClick={reRecord}
            disabled={disabled}
            leftIcon={<IconRefresh size={18} />}
          >
            {t("healthMemory.voiceNote.reRecordLabel")}
          </MButton>
        </div>
      )}

      {/* State 2: Recording — pulsing dot + MM:SS timer + Stop */}
      {state === "recording" && (
        <div className={styles.recordingRow}>
          <span className={`${styles.dot} ${styles.dotPulsing}`} />
          <span className={styles.timer}>{formatTime(elapsed)}</span>
          <MButton
            variant="outline"
            size="sm"
            onClick={stopRecording}
            disabled={disabled}
            leftIcon={<IconPlayerStop size={20} />}
          >
            {t("healthMemory.voiceNote.stopLabel")}
          </MButton>
        </div>
      )}

      {/* State 3: Preview — play/pause + elapsed/total + Re-record + Discard */}
      {state === "preview" && !disabled && (
        <div className={styles.recorderRoot}>
          <div className={styles.recordingRow}>
            <MButton
              variant="icon-only-outline"
              size="sm"
              onClick={handlePreviewPlayPause}
              aria-label={
                isPreviewPlaying
                  ? t("healthMemory.voiceNote.pauseAriaLabel")
                  : t("healthMemory.voiceNote.playAriaLabel")
              }
              leftIcon={
                isPreviewPlaying ? (
                  <IconPlayerPause size={20} />
                ) : (
                  <IconPlayerPlay size={20} />
                )
              }
            />
            <span className={styles.timer}>
              {formatTime(previewElapsed)}&nbsp;/&nbsp;{formatTime(elapsed)}
            </span>
            {/* Hidden audio element for preview playback (T-56-05) */}
            <audio
              ref={previewAudioRef}
              onEnded={() => {
                setIsPreviewPlaying(false);
                setPreviewElapsed(0);
              }}
            />
          </div>
          <div className={styles.previewControls}>
            <MButton
              variant="outline"
              size="sm"
              onClick={reRecord}
              leftIcon={<IconRefresh size={18} />}
            >
              {t("healthMemory.voiceNote.reRecordLabel")}
            </MButton>
            <MButton
              variant="destructive"
              size="sm"
              onClick={handleDiscard}
              leftIcon={<IconTrash size={18} />}
            >
              {t("healthMemory.voiceNote.discardLabel")}
            </MButton>
          </div>
        </div>
      )}

      {/* State 4: Uploading — form is submitting while in preview state */}
      {state === "preview" && disabled && <MLoader size={20} />}
    </div>
  );
};

export default VoiceNoteRecorder;
